import {
  Box,
  // eslint-disable-next-line no-restricted-imports
  Button as ChakraButton,
  Container,
  Flex,
  Heading,
  ListItem,
  Text,
  UnorderedList,
  VStack,
} from '@chakra-ui/react';
import { PRODUCT_IMAGES } from 'src/features/MP/utils/constants';
import { artworkCrop } from 'src/features/MP/utils/artworkCrop';
import { Header } from '../../atoms/Header';
import { RevealOnScroll } from '../../atoms/RevealOnScroll';
import { CroppedZoomImage } from '../../molecules/CroppedZoomImage';
import { useContactModal } from 'src/hooks/useContactModal';
import { ContactModal } from 'src/components/molecules/ContactModal';

const ARTWORK_CROP = {
  ProductImage1: artworkCrop(3744, 2400, 48, 40, 3647, 2303),
  ProductImage2: artworkCrop(2256, 1440, 48, 40, 2159, 1343),
  ProductImage3: artworkCrop(2256, 1440, 48, 40, 2159, 1343),
  ProductImage4: artworkCrop(2112, 2976, 48, 40, 2015, 2879),
};

const ProductsPage = () => {
  const { onOpen, modalProps } = useContactModal({
    defaultInterest: 'product',
    title: 'Start Modeling',
    submitButtonText: 'Start Modeling',
  });

  const { onOpen: onOpenSales, modalProps: salesModalProps } = useContactModal({
    defaultInterest: 'sales',
    title: 'Contact Sales',
    submitButtonText: 'Contact Sales',
  });

  return (
    <Box color="text.darkBlue.900">
      <Box bgColor="text.darkBlue.900">
        <Container py={{ base: 8, md: 16 }} maxW="container.xl">
          <Header color="text.lightGrey" textAlign="center">
            Move faster with less chaos.
            <Text
              fontSize="21px"
              color="text.lightGrey"
              fontFamily="marketingPage"
              fontWeight="normal"
              mt={4}
              mb={{ base: 8, md: 12 }}
              textTransform="none"
              textAlign="center">
              Manage tasks, priorities, and progress
              <br /> in a single organized place.
            </Text>
          </Header>
        </Container>
      </Box>
      <Container py={{ base: 8, md: 16 }} maxW="container.xl">
        <RevealOnScroll>
          <Flex
            direction={{ base: 'column', lg: 'row' }}
            justifyContent="space-between"
            w="100%"
            gap={{ base: 6, lg: 8 }}>
            <Header
              textAlign={{ base: 'center', lg: 'start' }}
              fontSize={{ base: '3xl', md: '4xl', lg: '4.5xl' }}>
              Smarter marketing <br /> decisions, powered by data
            </Header>
            <Flex
              flexDirection="column"
              width={{ base: '100%', lg: '35%' }}
              textAlign={{ base: 'center', lg: 'start' }}>
              <Text mb={2}>
                Merkatus brings together your online and offline marketing and
                sales data, transforming it into clear, actionable insights you
                can trust.
              </Text>
              <Text>
                See what works, what doesn&apos;t, and how to grow efficiently
                with the help of AI-driven analytics.
              </Text>
            </Flex>
          </Flex>
          <Flex
            mt={6}
            justifyContent={{ base: 'center', lg: 'end' }}
            alignItems="center">
            <CroppedZoomImage
              w={{ base: '100%', md: '600px', lg: '912px' }}
              src={PRODUCT_IMAGES.ProductImage1}
              crop={ARTWORK_CROP.ProductImage1}
              alt="Merkatus dashboards showing marketing performance across channels"
            />
          </Flex>
        </RevealOnScroll>

        <RevealOnScroll>
          <Header fontSize={{ base: '3xl', md: '4xl', lg: '4.5xl' }}>
            Understand what <br /> drives performance.
            <Text
              fontSize="md"
              fontFamily="marketingPage"
              fontWeight="normal"
              textTransform="none"
              mt={4}
              mb={{ base: 8, md: 12 }}
              textAlign={{ base: 'center', lg: 'start' }}>
              See the full picture with real-time insights across teams,
              campaigns, and channels. <br /> Spot trends, reduce waste, and
              move faster with confidence.
            </Text>
          </Header>
          <Flex
            mt={6}
            direction={{ base: 'column', lg: 'row' }}
            justifyContent="space-between"
            w="100%"
            gap={{ base: 8, lg: 0 }}>
            <Box
              width={{ base: '100%', lg: '50%' }}
              pt={{ base: 8, lg: 12 }}
              pr={{ base: 0, lg: 12 }}
              pb={{ base: 8, lg: 12 }}
              borderBottom="1px solid #F0F0F0"
              borderTop="1px solid #F0F0F0"
              borderRight={{ base: 'none', lg: '1px solid #F0F0F0' }}>
              <CroppedZoomImage
                w={{ base: '100%', md: '400px', lg: '540px' }}
                src={PRODUCT_IMAGES.ProductImage2}
                crop={ARTWORK_CROP.ProductImage2}
                alt="Metric filtering that drives action"
              />
              <Header fontSize={{ base: 'lg', md: 'xl' }} mt={6} mb={0}>
                Instant filtering that drives action{' '}
                <Text
                  fontSize="md"
                  fontFamily="marketingPage"
                  fontWeight="normal"
                  textTransform="none"
                  mt={4}
                  textAlign={{ base: 'center', lg: 'start' }}>
                  Click any metric to immediately explore detailed insights
                  behind it. <br />
                  No extra setup — just actionable data on the spot.
                </Text>
              </Header>
            </Box>
            <Box
              width={{ base: '100%', lg: '50%' }}
              pt={{ base: 8, lg: 12 }}
              pl={{ base: 0, lg: 12 }}
              pb={{ base: 8, lg: 12 }}
              borderBottom="1px solid #F0F0F0"
              borderTop="1px solid #F0F0F0">
              <CroppedZoomImage
                w={{ base: '100%', md: '400px', lg: '540px' }}
                src={PRODUCT_IMAGES.ProductImage3}
                crop={ARTWORK_CROP.ProductImage3}
                alt="Guided model setup"
              />
              <Header fontSize={{ base: 'lg', md: 'xl' }} mt={6} mb={0}>
                Model smarter, not harder
                <Text
                  fontSize="md"
                  fontFamily="marketingPage"
                  fontWeight="normal"
                  textTransform="none"
                  mt={4}
                  textAlign={{ base: 'center', lg: 'start' }}>
                  Quickly connect your data, choose your KPIs, and follow our
                  intuitive guide your through setup. <br /> <br />
                  Seamlessly connect your data, define your KPIs, and get guided
                  setup in minutes.
                </Text>
              </Header>
            </Box>
          </Flex>
        </RevealOnScroll>

        <RevealOnScroll>
          <Header
            fontSize={{ base: '3xl', md: '4xl', lg: '4.5xl' }}
            my={{ base: 16, md: 24, lg: 32 }}
            textAlign="center">
            Make better decisions with <br /> smarter effort tracking
          </Header>
          <Flex
            my={{ base: 16, md: 24, lg: 32 }}
            py={{ base: 8, lg: 12 }}
            direction={{ base: 'column', lg: 'row' }}
            borderY="1px solid #F0F0F0"
            justifyContent="space-between"
            gap={{ base: 8, lg: 0 }}>
            <Box width={{ base: '100%', lg: '50%' }}>
              <Header fontSize={{ base: '2xl', md: '3xl', lg: '4.5xl' }}>
                Understand how timing and saturation impact your marketing
                results
                <Text
                  fontSize="md"
                  fontFamily="marketingPage"
                  fontWeight="normal"
                  textTransform="none"
                  mt={4}
                  mb={4}
                  textAlign={{ base: 'center', lg: 'start' }}>
                  As a product or marketing lead, you need clarity on how
                  campaigns deliver over time and where spend starts to lose
                  efficiency. Merkatus.io gives you the views you need to stay
                  in control:
                </Text>
                <UnorderedList
                  spacing={3}
                  fontSize="md"
                  textTransform="none"
                  fontFamily="marketingPage"
                  fontWeight="normal">
                  <ListItem>
                    <Text
                      fontFamily="marketingPage"
                      fontWeight="bold"
                      as="span"
                      textTransform="none">
                      Flighting View:
                    </Text>{' '}
                    Compare burst vs. always-on strategies to find the pacing
                    that drives consistent impact.
                  </ListItem>
                  <ListItem>
                    <Text
                      fontFamily="marketingPage"
                      fontWeight="bold"
                      as="span"
                      textTransform="none">
                      S-Curve Analytics:
                    </Text>{' '}
                    See where additional spend stops generating proportional
                    returns and where to reallocate before ROI declines.
                  </ListItem>
                  <ListItem>
                    <Text
                      fontFamily="marketingPage"
                      fontWeight="bold"
                      as="span"
                      textTransform="none">
                      Actionable Insights:
                    </Text>{' '}
                    Pinpoint the right timing, intensity, and budget levels for
                    each channel.
                  </ListItem>
                  <ListItem>
                    <Text
                      fontFamily="marketingPage"
                      fontWeight="bold"
                      as="span"
                      textTransform="none">
                      Scenario Testing:
                    </Text>{' '}
                    Explore pacing and spend alternatives before committing real
                    investment.
                  </ListItem>
                </UnorderedList>
                <Text
                  fontSize="md"
                  fontFamily="marketingPage"
                  fontWeight="normal"
                  textTransform="none"
                  mt={4}
                  mb={4}
                  textAlign={{ base: 'center', lg: 'start' }}>
                  With these tools, you can finally connect timing, saturation,
                  and business results, turning data into confident decisions.
                </Text>
              </Header>{' '}
            </Box>
            <Box width={{ base: '100%', lg: '40%' }}>
              <CroppedZoomImage
                w={{ base: '100%', md: '400px', lg: '504px' }}
                src={PRODUCT_IMAGES.ProductImage4}
                crop={ARTWORK_CROP.ProductImage4}
                alt="Timing and saturation analytics"
              />
            </Box>
          </Flex>
        </RevealOnScroll>

        <RevealOnScroll>
          <VStack spacing={4} px={4} textAlign="center" mb={16}>
            <Heading
              color={'text.darkBlue.900'}
              fontSize={{ base: '2xl', md: '3xl', lg: '4.5xl' }}
              textTransform="uppercase"
              fontFamily="marketingPage">
              Ready to get real answers <br /> from your data?{' '}
            </Heading>
            <Text
              fontSize={{ base: 'lg', md: 'xl' }}
              color="text.darkBlue.800"
              fontWeight={500}>
              Start modeling with AI-powered insights or talk to <br />
              our team to find the best setup for your business.
            </Text>
            <Flex
              direction={{ base: 'column', sm: 'row' }}
              gap={4}
              w={{ base: '100%', sm: 'auto' }}>
              <ChakraButton
                onClick={onOpen}
                size="lg"
                bg={'icon.green'}
                color="white"
                fontWeight="bold"
                fontFamily="marketingPage"
                borderRadius="8px"
                px={8}
                w={{ base: '100%', sm: 'auto' }}
                _hover={{ bg: 'icon.green.500' }}>
                Start modeling
              </ChakraButton>
              <ChakraButton
                onClick={onOpenSales}
                size="lg"
                bg={'background.lightGrey'}
                border="1px solid #F0F0F0"
                color="text.darkBlue.900"
                fontFamily="marketingPage"
                borderRadius={8}
                px={8}
                py={4}
                w={{ base: '100%', sm: 'auto' }}>
                Contact Sales &nbsp; →
              </ChakraButton>
            </Flex>
          </VStack>
        </RevealOnScroll>
      </Container>
      <ContactModal {...modalProps} />
      <ContactModal {...salesModalProps} />
    </Box>
  );
};

export default ProductsPage;
